'use client';

import { useEffect } from 'react';

export interface IPdpContentWidthSyncProps {
  selectorElementSource: string | string[];
  selectorElementTarget: string;
  customMediaQuery?: string;
  customTargetMinWidth?: number;
  customAdditionalSize?: number;
}

export const PdpContentWidthSync = ({
  selectorElementSource,
  selectorElementTarget,
  customMediaQuery,
  customTargetMinWidth,
  customAdditionalSize = 0,
}: IPdpContentWidthSyncProps) => {
  useEffect(() => {
    const content = document.querySelector<HTMLElement>(selectorElementTarget);
    // Selectors that match nothing are ignored (e.g. only one of the image elements is rendered at a time)
    const elements = (Array.isArray(selectorElementSource) ? selectorElementSource : [selectorElementSource])
      .map((selector) => document.querySelector<HTMLElement>(selector))
      .filter((element): element is HTMLElement => !!element);

    if (!content || !elements.length) return;

    const mediaQuery = window.matchMedia(customMediaQuery ?? '(min-width: 1200px)');

    // With several sources they are laid out in a row: width = sum of their widths + the parent's column gaps
    // + the target's own horizontal padding (box-sizing: border-box)
    const getSourcesWidth = () => {
      const parent = elements[0].parentElement;
      const gap = parent ? parseFloat(getComputedStyle(parent).columnGap) || 0 : 0;
      const sourcesWidth = elements.reduce((total, element) => total + element.offsetWidth, 0);

      if (elements.length === 1) return sourcesWidth;

      const contentStyles = getComputedStyle(content);
      const padding = parseFloat(contentStyles.paddingLeft) + parseFloat(contentStyles.paddingRight);

      return sourcesWidth + gap * (elements.length - 1) + padding;
    };

    const updateWidth = () => {
      const width = getSourcesWidth() + customAdditionalSize;

      content.style.width = mediaQuery.matches
        ? `${customTargetMinWidth ? Math.max(customTargetMinWidth, width) : width}px`
        : '';
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);

    elements.forEach((element) => observer.observe(element));
    mediaQuery.addEventListener('change', updateWidth);
    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', updateWidth);
      window.removeEventListener('resize', updateWidth);
      content.style.width = '';
    };
  }, []);

  return null;
};
